import axios from 'axios';
import { DATA_SALT, KEY_ACCESS_TOKEN, KEY_REFRESH_TOKEN, KEY_USER_DETAILS } from './Constants/constant';
import { captureException } from '@sentry/react';
import { AES, enc } from 'crypto-js';
export let axiosInstance;

// Base URL comes from the build-time environment variable so the same bundle
// can be promoted across environments. Falls back to the previous hardcoded
// dev URL so nothing breaks if the env var isn't set.
const API_BASE_URL =
  process.env.REACT_APP_BASE_URL ||
  'https://iimu-node-api-dev.ken42.com';

export const getAxiosInstance = () => {
  axiosInstance = axios.create({
    // baseURL: CONFIGS.BASE_URL,
    baseURL: 'https://iimu-api-preprod.thekenverse.com/', 
    // baseURL: 'http://localhost:4000',
    timeout: 25000,
    // Sends HttpOnly auth cookies issued by the Node backend. Harmless while
    // sessionStorage-based auth is still the primary path; becomes the
    // primary path once the backend cookie rollout is verified in every env.
    withCredentials: true,
  });

  axiosInstance.interceptors.request.use((req) => {
    // Legacy bearer auth — kept as a fallback during the cookie migration.
    // Once every environment confirms cookies work, this block can be removed
    // and the KEY_ACCESS_TOKEN storage in authContext.js dropped.
    const token = sessionStorage.getItem(KEY_ACCESS_TOKEN);
    if (token) {
      req.headers.Authorization = `Bearer ${token}`;
      req.headers['access-token'] = token;
    }
    return req;
  });

  //Response interceptor for API calls
  axiosInstance.interceptors.response.use(
    (resp) => {
      return resp;
    },
    async function (error) {
      captureException(error);
      if (!error.response?.config && error.response?.status !== 401) {
        return error;
      }

      if (error.response?.status === 400 || error.response?.status === 404 || error.response?.status === 500) {
        return error;
      }

      if (sessionStorage.refresh_token && (error.response.status === 402 || error.response.status === 502) && error?.response?.data?.error_code == 'jwt_token_expired') {
        try {
          const res = await refreshToken(sessionStorage.getItem(KEY_REFRESH_TOKEN));
          sessionStorage.setItem(KEY_ACCESS_TOKEN, res?.data?.accessToken?.token);
          sessionStorage.setItem(KEY_REFRESH_TOKEN, res?.data?.refreshToken?.token);
          error.response.config.headers['Authorization'] = `Bearer ${res?.data?.accessToken?.token}`;
          error.response.config.headers['access-token'] = sessionStorage.getItem(KEY_ACCESS_TOKEN);
          return axios(error.response.config);
        } catch (err) {
          sessionStorage.clear();
          window.location.reload();
        }
      } else {
        if (error?.response?.data?.error_code == 'jwt_token_exist') {
          const getSession = sessionStorage.getItem('session');
          if (!getSession) {
            window.location.reload();
          }
          captureException(error);
          sessionStorage.setItem('session', true);
        } else {
          captureException(error);
          sessionStorage.clear();
          window.location.reload();
          return axios(error.response.config);
        }
      }
    }
  );
};

export const refreshToken = async (token) => {
  // const path = 'http://localhost:4000/api/app/refreshToken';
  const path = 'https://iimu-api-preprod.thekenverse.com/api/app/refreshToken'; 
  const user = sessionStorage.getItem(KEY_USER_DETAILS);
  const parseData = AES.decrypt(user, DATA_SALT).toString(enc.Utf8);
  const parsedUser = JSON.parse(parseData);

  if (!parsedUser?.user) {
    window.location.reload();
    sessionStorage.clear();
    return;
  }

  const response = await axios.post(path, {
    refreshToken: token,
    user: parsedUser?.user,
  });
  return response;
};

export const razorpayInitiate = async (data) => {
  const path = `/v1/razorpay/initiate`;
  const response = await axiosInstance.post(`${path}`, data);
  return response && response.data ? response.data : null;
};

export const verifyRazorpay = async (order_id, razorpay_payment_id, razorpay_signature, date) => {
  const path = `/v1/razorpay/verifyOrderSuccess?order_id=${order_id}&razorpay_payment_id=${razorpay_payment_id}&razorpay_signature=${razorpay_signature}`;
  const response = await axiosInstance.get(`${path}`);
  return response && response.data ? response.data : null;
};

export const getAllPrograms = async (applicantId) => {
  const path = `v1/forms/getAllForms`;
  const res = await axiosInstance.get(`${path}`);
  return res && res.data ? res.data : null;
};

export const getFormByFormId = async (formId) => {
  const path = `v1/forms/getFormsById?id=${formId}`;
  const res = await axiosInstance.get(`${path}`);
  return res && res.data ? res.data : null;
};

export const getApplicationsByApplicantId = async (applicantid) => {
  const path = `/v1/application/getAllApplications?applicantid=${applicantid}`;
  const res = await axiosInstance.get(`${path}`);
  return res && res.data ? res.data : null;
};

export const getApplicationByApplicationId = async (applicationId) => {
  const path = `/v1/application/getApplication?applicationid=${applicationId}`;
  const res = await axiosInstance.get(`${path}`);
  return res && res.data ? res.data : null;
};

export const createUpdateApplication = async (data) => {
  const path = `/v1/application/portal/save`;
  const res = await axiosInstance.post(`${path}`, data);
  return res;
};

export const deleteApplicationRecords = async (type, id) => {
  const path = `/v1/application/portal/delete?type=${type}&id=${id}`;
  const res = await axiosInstance.delete(`${path}`);
  return res && res.data ? res.data : null;
};

export const employmentRecords = async () => {
  const path = `/v1/application/employment/records`;
  const res = await axiosInstance.get(`${path}`);
  return res && res.data ? res.data : null;
};

export const contactRecords = async () => {
  const path = `/v1/application/contact/records`;
  const res = await axiosInstance.get(`${path}`);
  return res && res.data ? res.data : null;
};

export const getDocuments = async (applicantId) => {
  const path = `/v1/application/documents?applicantId=${applicantId}`;
  const res = await axiosInstance.get(`${path}`);
  return res && res.data ? res.data : null;
};

export const createDocuments = async (data) => {
  const path = '/v1/application/portal/save';
  const res = await axiosInstance.post(`${path}`, data);
  return res && res.data ? res.data : null;
};

export const getDocumentUrl = async (formData, appId) => {
  const path = `/v1/application/upload/${appId}/document`;
  const res = await axiosInstance.post(`${path}`, formData);
  return res && res.data ? res.data : null;
};

export const getOfferLetter = async (applicantId) => {
  const path = `/v1/application/get/offerLetter?applicantId=${applicantId}`;
  const res = await axiosInstance.get(`${path}`);
  return res && res.data ? res.data : null;
};

export const getOfferLetterData = async (applicationId) => {
  const path = `/v1/application/get/offerLetterData?applicationId=${applicationId}`;
  const res = await axiosInstance.get(`${path}`);
  return res && res.data ? res.data : null;
};

export const getOfferLetterDownload = async (applicationId) => {
  const path = `/v1/application/get/offerLetter/download?applicationId=${applicationId}`;
  const res = await axiosInstance.get(`${path}`);
  return res && res.data ? res.data : null;
};

export const getFeeAssignments = async (applicantid) => {
  const path = `/v1/application/get/feeAssignments?applicantid=${applicantid}`;
  const res = await axiosInstance.get(`${path}`);
  return res && res.data ? res.data : null;
};

export const saveOfferLetterResponse = async (data) => {
  const path = `/v1/application/save/offerStatus`;
  const res = await axiosInstance.post(`${path}`, data);
  return res && res.data ? res.data : null;
};

export const getFeeDetails = async (applicationId) => {
  const path = `/v1/application/feeAssignmentId/${applicationId}`;
  const res = await axiosInstance.get(`${path}`);
  return res && res.data ? res.data : null;
};

export const saveWithdrawRequest = async (data) => {
  const path = `/v1/application/withdraw/request`;
  const res = await axiosInstance.post(`${path}`, data);
  return res && res.data ? res.data : null;
};
export const getDownlaodReceipt = async (receiptId) => {
  const path = `/v1/application/downloadPaymentReceipt/${receiptId}`;
  const res = await axiosInstance.get(path, {
    responseType: 'arraybuffer',
  });
  return res;
};

export const getApplicationPreviewPdf = async (applicationId) => {
  const path = `/v1/application/preview/pdf?applicationId=${applicationId}`;
  const res = await axiosInstance.get(`${path}`, {
    responseType: 'arraybuffer',
  });
  return res;
};

export const getPreApplicationPreviewPdf = async (applicationId) => {
  const path = `/v1/application/preview/pdf/pre-enrollment?applicationId=${applicationId}`;
  const res = await axiosInstance.get(`${path}`, {
    responseType: 'arraybuffer',
  });
  return res;
};


export const saveFeePayments = async (data) => {
  try {
    const path = '/v1/application/save/payment';
    const res = await axiosInstance.post(`${path}`, data);
      if (res && res.data) {
        return { success: true, data: res.data };
      } else {
        return { success: false, error: res?.response };
      }
  } catch (error) {
    console.log(error, 'error');
    return error;
  }
};

export const postInitatePayment = async (data) => {
  const path = `payments/flywire/initiate`;
  const res = await axiosInstance.post(`${path}`, data);
  return res && res.data ? res.data : null;
};

export const getPaymentTransactions = async (applicantId) => {
  const path = `/v1/application/transactions?applicantId=${applicantId}`;
  const res = await axiosInstance.get(`${path}`);
  return res && res.data ? res.data : null;
};

export const createEMBAToken = async (id) => {
  const path = `/v1/application/createtoken/emba`;
  const res = await axiosInstance.get(`${path}?id=${id}`);
  return res && res.data ? res.data : null;
};

export const verifyEMBAToken = async (token, appId, expiry) => {
  const path = `/v1/application/verifytoken/emba?token=${encodeURIComponent(token)}&appId=${appId}&tokenExpiry=${expiry}`;
  const res = await axiosInstance.get(`${path}`);
  return res && res.data ? res.data : null;
};

export const updateLead = async (data) => {
  const path = `update/lead?contact=${data}`;
  const res = await axiosInstance.post(`${path}`);
  return res && res.data ? res.data : null;
};

// create lead
export const createLead = async (data) => {
  try {
    const path = `v1/lead/create`;
    const res = await axiosInstance.post(`${path}`, data);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'error');
    return error;
  }
};

export const getLeadDetails = async (email) => {
  try {
    console.log(email, 'email');
    const path = '/v1/lead/get/details';
    const res = await axiosInstance.get(`${path}?email=${email}`);
    console.log(res, 'dataRes');
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'error');
    return error;
  }
};

// Verification Lead
export const verificationLead = async (token) => {
  try {
    console.log('tokenAPIData', token);
    const path = `v1/lead/verification?token=${token}`;
    const res = await axiosInstance.get(`${path}`);
    console.log('respath', res);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'error');
    return error;
  }
};

export const authenticateEmailPasswordLead = async (payload) => {
  const path = `v1/lead/authenticate`;
  const res = await axiosInstance.post(`${path}`, payload);
  return res && res.data ? res.data : null;
};

export const forgotPassword = async (email) => {
  const path = `v1/lead/forgot/password`;
  const res = await axiosInstance.post(`${path}?email=${email}`);
  return res && res.data ? res.data : null;
};

export const resendVerificationLink = async (email) => {
  const path = `v1/lead/resend/verification`;
  const res = await axiosInstance.post(`${path}?email=${email}`);
  return res && res.data ? res.data : null;
};

export const resetPassword = async (payload) => {
  const path = `v1/lead/reset/password`;
  const res = await axiosInstance.post(`${path}`, payload);
  return res && res.data ? res.data : null;
};

// Calls the server-side logout endpoint which clears the HttpOnly auth
// cookies. JS cannot clear HttpOnly cookies itself, which is why this
// round-trip is required. Safe to call even if the session is already expired.
export const logoutOnServer = async () => {
  try {
    const path = 'v1/lead/logout';
    await axiosInstance.post(path);
  } catch (_err) {
    // Logout should never fail visibly to the user — worst case the cookie
    // is already gone or expired.
  }
};

export const postcreateCase = async (data) => {
  const path = `/v1/query/create`;
  const res = await axiosInstance.post(`${path}`, data);
  return res && res.data ? res.data : null;
};
export const getAllQueryByApplicantId = async (applicantid) => {
  const path = `/v1/query/getAllQuery?applicantid=${applicantid}`;
  const res = await axiosInstance.get(`${path}`);
  return res && res.data ? res.data : null;
};
export const postopenNotification = async (data) => {
  try {
    const path = `v1/query/openNotification`;
    const res = await axiosInstance.post(`${path}`, data);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    console.log(error, 'error');
    return error;
  }
};

export const getStudentInterviewSlot = async (contactId) => {
  const path = 'v1/interview/get/student/slot';
  const res = await axiosInstance.get(`${path}?contactId=${contactId}`);
  return res && res.data ? res.data : null;
};

export const getSlots = async (applicationFormId) => {
  try {
    const path = 'v1/interview/get/slots';
    const res = await axiosInstance.get(`${path}?applicationFormId=${applicationFormId}`);
    console.log(res, 'DatResd');
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    return error;
  }
};

export const updateSlots = async (body) => {
  try {
    const path = 'v1/interview/update/interviewslot';
    const res = await axiosInstance.post(`${path}`, body);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    return error;
  }
};
 
export const smartCollectBankTransfer = async (data) => {
  try {
    const path = 'v1/razorpay/virtual_accounts';
    const res = await axiosInstance.post(`${path}`, data);
    if (res && res.data) {
      return { success: true, data: res.data };
    } else {
      return { success: false, error: res?.response };
    }
  } catch (error) {
    return error;
  }
};

export const getOnboardingPicklists = async () => {
  const path = `/v1/application/onboarding/picklists`;
  const res = await axiosInstance.get(path);
  return res.data;
};

export const saveCrossOfferDetails = async (data) => {
  const path = `/v1/application/onboarding/crossOffer`;
  const res = await axiosInstance.post(path, data);
  return res;
};

export const saveOnboardingApplication = async (data) => {
  const path = `/v1/application/onboarding/save`;
  const res = await axiosInstance.post(path, data);
  return res;
};

export const downloadTravelGatePass = async (applicationId) => {
  const path = `/v1/application/onboarding/gatePass?applicationId=${applicationId}`;
  const res = await axiosInstance.get(path, { responseType: 'arraybuffer' });
  return res;
};

export const getTravelGatePassData = async (applicationId) => {
  const path = `/v1/application/onboarding/gatePassData/${applicationId}`;
  const res = await axiosInstance.get(path);
  return res.data;
};
